import { useEffect, useRef, useState } from 'react';

// Single shared IntersectionObserver instance, lazily created. We
// don't want one observer per element — that's expensive for pages
// with many revealed sections. Instead every component subscribes
// via a callback map keyed by the observed element.
let sharedObserver = null;
const callbacks = new WeakMap();

function getObserver() {
  if (sharedObserver) return sharedObserver;
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const cb = callbacks.get(entry.target);
        if (cb && entry.isIntersecting) {
          cb(true);
          // One-shot reveal — once visible, stop observing so the
          // section doesn't fade back out on scroll. If we ever want
          // a re-trigger mode, gate this behind an option.
          sharedObserver.unobserve(entry.target);
          callbacks.delete(entry.target);
        }
      }
    },
    {
      rootMargin: '0px 0px -10% 0px', // start the reveal slightly before the section hits the viewport
      threshold: 0.1,
    }
  );
  return sharedObserver;
}

// useReveal — returns a [ref, isVisible] tuple. Attach the ref to any
// element; the boolean flips true when the element scrolls into view
// and stays true forever (no flicker on bounce). Pair with the global
// .reveal / .reveal.is-visible CSS classes for the actual animation.
export default function useReveal({ disabled = false } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(disabled); // skip animation when disabled (e.g. above-the-fold content)

  useEffect(() => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    const observer = getObserver();
    if (!observer) {
      // No IntersectionObserver support — fall back to "always visible"
      // so we don't hide content on legacy browsers.
      setVisible(true);
      return;
    }
    callbacks.set(el, setVisible);
    observer.observe(el);
    return () => {
      callbacks.delete(el);
      observer.unobserve(el);
    };
  }, [disabled]);

  return [ref, visible];
}
